import { KatFileUpload } from '@amzn/katal-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { reset, uploadImages } from 'src/redux/slices/image';
import { AppState } from 'src/redux/store';
import FetchStatus from 'src/types/FetchStatus';
import { ImageTypes, ImageUploadError } from 'src/types/ImageUpload';

export enum KatFileUploadStatus {
  ready = 'ready',
  uploading = 'uploading',
  complete = 'complete',
  error = 'error',
}

// 4 MB
export const maxFileByteSize = 4000000;
const supportedFileTypes = ['image/jpg', 'image/jpeg', 'image/png'];

const isFilesReady = (files: FilesState) => Object.values(files).some(
  (file) => file.status === KatFileUploadStatus.ready,
);

export type FileState = {
  file: File,
  status: KatFileUploadStatus,
  url?: string,
  error?: string,
};

export type FilesState = {
  [key: string]: FileState
};

export type ImageUploadProps = {
  onFilesAttached?: (files: FilesState) => void,
  onFilesRemoved?: (files: FilesState) => void,
  maxImagesAllowed: number,
  imageSizeText?: string,
  imageFormatText: string,
  imageType: ImageTypes,
};

const errorMessageLookup: Record<ImageUploadError, string> = {
  [ImageUploadError.presignedFailed]: 'imageUpload_attach_error_uploadFail',
  [ImageUploadError.imageNotFound]: 'imageUpload_attach_error_uploadFail',
  [ImageUploadError.inappropriate]: 'imageUpload_attach_error_inappropriate',
  [ImageUploadError.tooLarge]: 'imageUpload_attach_error_size',
  [ImageUploadError.unsupportedType]: 'imageUpload_attach_error_format',
};

const ImageUpload = ({
  maxImagesAllowed,
  onFilesAttached,
  onFilesRemoved,
  imageSizeText,
  imageFormatText,
  imageType,
}: ImageUploadProps) => {
  const { t } = useTranslation();
  const [fileUploadState, setFileUploadState] = useState<KatFileUpload.KatFileUploadState>({
    status: KatFileUploadStatus.ready,
  });
  const [files, setFiles] = useState<FilesState>({});
  const [filesReady, setFilesReady] = useState(false);
  const dispatch = useAppDispatch();
  const imageUploadStatus = useAppSelector(({ image }: AppState) => image.status);
  const uploadedImages = useAppSelector(({ image }: AppState) => image.images);
  const filesNames = Object.keys(files);
  const filesLength = filesNames.length;

  useEffect(() => () => { dispatch(reset()); }, []);

  useEffect(() => {
    if (filesReady) {
      const readyNames = filesNames.filter(
        (name) => files[name].status === KatFileUploadStatus.ready,
      );
      if (filesLength > 0 && filesLength <= maxImagesAllowed) {
        const uploadingFiles: FilesState = {};
        readyNames.forEach((name) => {
          uploadingFiles[name] = {
            file: files[name].file,
            status: KatFileUploadStatus.uploading,
          };
        });
        setFiles((prev) => ({ ...prev, ...uploadingFiles }));
        dispatch(uploadImages({ files: uploadingFiles, imageType })).then(() => {
          setFilesReady(false);
        });
      } else {
        const errorFiles: FilesState = {};
        readyNames.forEach((name) => {
          errorFiles[name] = {
            file: files[name].file,
            status: KatFileUploadStatus.error,
          };
        });
        setFiles((prev) => ({ ...prev, ...errorFiles }));
        setFileUploadState({
          status: KatFileUploadStatus.error,
          error: t('imageUpload_attach_error_fileLimit') as string,
        });
        setFilesReady(false);
      }
    }
  }, [filesReady]);

  useEffect(() => {
    if ([FetchStatus.complete, FetchStatus.failed].includes(imageUploadStatus)) {
      const uploadedFiles: FilesState = {};
      Object.keys(uploadedImages).forEach((name) => {
        let status = KatFileUploadStatus.complete;
        let error;
        if (uploadedImages[name].status === FetchStatus.failed) {
          status = KatFileUploadStatus.error;
          error = t(errorMessageLookup[uploadedImages[name].error]);
        }

        if (files[name]) {
          uploadedFiles[name] = {
            file: files[name].file,
            status,
            url: uploadedImages[name].url,
            error,
          };
        }
      });

      setFiles((prev) => ({ ...prev, ...uploadedFiles }));
      if (onFilesAttached) onFilesAttached(uploadedFiles);
      if (imageUploadStatus === FetchStatus.failed) {
        setFileUploadState({ status: KatFileUploadStatus.error });
      }
    } else if (imageUploadStatus === FetchStatus.loading) {
      setFileUploadState({ status: KatFileUploadStatus.ready });
    }
  }, [imageUploadStatus]);

  const handleAddFiles = (filesList: File[]) => {
    const newFiles: FilesState = filesList.reduce((acc, file) => {
      const exceedsMaxSize = file.size > maxFileByteSize;
      const isIncorrectType = !supportedFileTypes.includes(file.type);

      let error;
      if (isIncorrectType) {
        error = t('imageUpload_attach_error_format');
      } else if (exceedsMaxSize) {
        error = t('imageUpload_attach_error_size');
      }

      return {
        ...acc,
        [file.name]: {
          file,
          status: exceedsMaxSize || isIncorrectType
            ? KatFileUploadStatus.error : KatFileUploadStatus.ready,
          error,
        },
      };
    }, {});
    setFiles((prevFiles) => ({ ...prevFiles, ...newFiles }));
    setFilesReady(isFilesReady(newFiles));
    if (onFilesAttached) onFilesAttached(newFiles);
  };

  const handleRemoveFiles = (filesList: File[]) => {
    const oldFilesLength = filesLength;
    const newFiles = files;
    filesList.forEach((file) => {
      delete newFiles[file.name];
    });

    if (oldFilesLength > maxImagesAllowed) {
      const newFilesNames = Object.keys(newFiles);
      const newFilesLength = newFilesNames.length;
      if (newFilesLength <= maxImagesAllowed && newFilesLength !== 0) {
        newFilesNames.forEach((name) => {
          const { status, error } = newFiles[name];
          // Only ready files that have already passed size/type validations
          if (status === KatFileUploadStatus.error && !error) {
            newFiles[name].status = KatFileUploadStatus.ready;
          }
        });
      }
    }

    setFiles(() => ({ ...newFiles }));
    setFilesReady(isFilesReady(newFiles));
    if (onFilesRemoved) onFilesRemoved(newFiles);
  };

  const uploadFilesDisabled = imageUploadStatus === FetchStatus.loading
    || filesLength >= maxImagesAllowed;

  return (
    <KatFileUpload
      multiple={maxImagesAllowed > 1}
      disabled={uploadFilesDisabled}
      state={{ fileUploadState }}
      accept={supportedFileTypes.join(',')}
      fileStates={{
        fileItemUpdateStates: Object.values(files).map(({ file, status, error }) => ({
          name: file.name,
          status,
          errorSummary: error,
        })) as KatFileUpload.KatFileItemUpdateState[],
      }}
      onFilesAttached={(e) => { handleAddFiles(e.detail.files); }}
      onFilesReplaced={(e) => { handleAddFiles(e.detail.newFiles); }}
      onFilesRemoved={(e) => { handleRemoveFiles(e.detail.files); }}
    >
      <div slot="hint">{t('imageUpload_attach_hint')}</div>
      <div slot="constraints-message">
        {imageFormatText}
        <br />
        {imageSizeText}
      </div>
    </KatFileUpload>
  );
};

ImageUpload.defaultProps = {
  onFilesAttached: undefined,
  onFilesRemoved: undefined,
  imageSizeText: undefined,
};

export default ImageUpload;
